<template>
    <div class="adjustment-modal">
        <!-- Header -->
        <div class="modal-header">
            <div class="header-content">
                <p class="client-name">
                    <span class="label">Client:</span>
                    <span class="value">{{ currentClient?.fullname_client }}</span>
                </p>
            </div>
            <div class="client-id-badge" v-if="currentClient?.userpk_client">
                ID: {{ currentClient.userpk_client }}
            </div>
        </div>

        <!-- Form -->
        <form @submit.prevent="handleSubmit" class="adjustment-form">
            <div class="row g-4">
                <!-- Adjustment Type -->
                <div class="col-12">
                    <div class="form-group">
                        <label class="form-label">
                            <span class="label-icon">📋</span>
                            Adjustment Type
                        </label>
                        <div class="type-selector">
                            <button type="button" class="type-option"
                                :class="{ 'active': transaction.module_te === 'ADD-ON' }"
                                @click="transaction.module_te = 'ADD-ON'">
                                <span class="option-icon">➕</span>
                                <span class="option-text">Add-on</span>
                                <span class="option-desc">Additional charge</span>
                            </button>
                            <button type="button" class="type-option"
                                :class="{ 'active': transaction.module_te === 'DISCOUNT' }"
                                @click="transaction.module_te = 'DISCOUNT'">
                                <span class="option-icon">💰</span>
                                <span class="option-text">Discount</span>
                                <span class="option-desc">Reduce bill amount</span>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Amount -->
                <div class="col-md-6">
                    <div class="form-group">
                        <label class="form-label">
                            <span class="label-icon">💵</span>
                            Amount
                        </label>
                        <div class="amount-input-group">
                            <span class="currency-symbol">₱</span>
                            <input ref="amountRef" type="number" v-model="transaction.amount_te" step="0.01" min="0"
                                class="form-control amount-input text-end" :class="{ 'error': amountError }" placeholder="0.00"
                                required />
                        </div>
                        <span v-if="amountError" class="error-text">{{ amountError }}</span>
                        <small class="form-text text-muted">
                            {{ transaction.module_te === 'ADD-ON' ? 'Amount to add to bill' : 'Amount to deduct from bill' }}
                        </small>
                    </div>
                </div>
                <!-- Date Issued -->
                <div class="col-md-6">
                    <div class="form-group">
                        <label class="form-label">
                            <span class="label-icon">📝</span>
                            Date Issued
                        </label>
                        <input type="date" v-model="transaction.dateissued_te" class="form-control" required="true"
                            placeholder="Enter reference or remarks" />
                        <small class="form-text text-muted">* Date of issuance</small>
                    </div>
                </div>
                <!-- Reference/Remarks -->
                <div>
                    <div class="form-group">
                        <label class="form-label">
                            <span class="label-icon">📝</span>
                            Reference / Remarks
                        </label>
                        <textarea v-model="transaction.transref_te" class="form-control" required="true"
                            placeholder="Enter reference or remarks"></textarea>
                        <small class="form-text text-muted">* Add note or reference number</small>
                    </div>
                </div>

                <!-- Current Bill Summary (if available) -->
                <div class="col-12" v-if="currentClient?.balance_client">
                    <div class="bill-summary">
                        <div class="summary-item">
                            <span class="summary-label">Current Balance</span>
                            <span class="summary-value">{{ formatCurrency(currentClient.balance_client) }}</span>
                        </div>
                        <div class="summary-item" v-if="transaction.amount_te > 0">
                            <span class="summary-label adjustment-label">
                                {{ transaction.module_te === 'ADD-ON' ? '➕ Add-on' : '➖ Discount' }}
                            </span>
                            <span class="summary-value"
                                :class="transaction.module_te === 'ADD-ON' ? 'text-danger' : 'text-success'">
                                {{ transaction.module_te === 'ADD-ON' ? '+' : '-' }}{{
                                formatCurrency(transaction.amount_te) }}
                            </span>
                        </div>
                        <div class="summary-item total" v-if="transaction.amount_te > 0">
                            <span class="summary-label">New Balance</span>
                            <span class="summary-value total-value">{{ formatCurrency(newMonthlyDue) }}</span>
                        </div>
                    </div>
                </div>

                <!-- Preview Effect -->
                <div class="col-12" v-if="transaction.amount_te > 0">
                    <div class="preview-card"
                        :class="transaction.module_te === 'ADD-ON' ? 'preview-addon' : 'preview-discount'">
                        <div class="preview-icon">
                            {{ transaction.module_te === 'ADD-ON' ? '➕' : '➖' }}
                        </div>
                        <div class="preview-content">
                            <h4>Preview Effect</h4>
                            <p>
                                {{ transaction.module_te === 'ADD-ON'
                                    ? `This will increase the client's monthly bill by
                                ${formatCurrency(transaction.amount_te)}`
                                    : `This will reduce the client's monthly bill by
                                ${formatCurrency(transaction.amount_te)}`
                                }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Form Actions -->
            <div class="form-actions">
                <button type="button" class="btn btn-outline-secondary" @click="handleCancel">
                    <span class="btn-icon">✖</span>
                    Cancel
                </button>
                <button type="submit" class="btn btn-primary" :disabled="!isValid">
                    <span class="btn-icon">✓</span>
                    Apply Adjustment
                </button>
            </div>
        </form>
    </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import Formatters from '@/utils/formatters';
import confirmationService from '@/services/confirmationService';
import transactionService from '@/services/transactionService';
import toastService from '@/services/toastService';
/* eslint-disable */
const currentClient = ref({});
const emit = defineEmits(['adjustment-applied', 'cancel']);
const amountRef = ref(null);
const transaction = ref({
    sysfk_client_te: 0,
    module_te: 'DISCOUNT',
    amount_te: 0,
    transref_te: '',
    dateissued_te: new Date().toISOString().split('T')[0]
});

const amountError = ref('');

// Set client data
const setClient = (client) => {
    currentClient.value = { ...client };
    transaction.value.sysfk_client_te = client.userpk_client || 0;
    nextTick(()=>{
        amountRef.value.focus();
        amountRef.value.select();
    });
};

// Computed properties
const isValid = computed(() => {
    return transaction.value.amount_te > 0;
});

const newMonthlyDue = computed(() => {
    if (!currentClient.value.balance_client || !transaction.value.amount_te) {
        return currentClient.value.monthlydue_client || 0;
    }

    if (transaction.value.module_te === 'ADD-ON') {
        return currentClient.value.balance_client + transaction.value.amount_te;
    } else {
        return Math.max(0, currentClient.value.balance_client - transaction.value.amount_te);
    }
});

// Validation
watch(() => transaction.value.amount_te, (newVal) => {
    if (newVal <= 0) {
        amountError.value = 'Amount must be greater than 0';
    } else {
        amountError.value = '';
    }
});

// Format currency
const formatCurrency = (value) => {
    return Formatters?.formatCurrency ? Formatters.formatCurrency(value) : `₱${value?.toFixed(2) || '0.00'}`;
};

// Handlers
const handleSubmit = async () => {
    if (!isValid.value) return;
    const confirmed = await confirmationService.show("Are you sure you want to apply this adjustment?");
    if (!confirmed)
        return;

    const adjustmentData = {
        ...transaction.value,
        sysfk_client_te: currentClient.value.syspk_client
    };
    const resp = await transactionService.applyAdjustment(adjustmentData);
    if(resp.code > -1){
        toastService.success("Adjustment applied sucessfully!")
        emit('adjustment-applied',currentClient.value)
    }
    else{
        toastService.error(resp.message);
    }
    //emit('save', adjustmentData);
};

const handleCancel = () => {
    emit('cancel');
};

// Expose methods
defineExpose({ setClient });
</script>

<style scoped>
.adjustment-modal {
    max-width: 600px;
    margin: 0 auto;
    padding: 24px;
    background: #fff;
    border-radius: 16px;
}

/* Header Styles */
.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 28px;
    padding-bottom: 16px;
    border-bottom: 2px solid #E5E7EB;
}

.header-content h2 {
    font-size: 24px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px 0;
}

.client-name {
    margin: 0;
    font-size: 16px;
    color: #4B5563;
}

.client-name .label {
    font-weight: 500;
    margin-right: 4px;
}

.client-name .value {
    font-weight: 600;
    color: #0A5C9E;
}

.client-id-badge {
    background: #F3F4F6;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 600;
    color: #4B5563;
    border: 1px solid #E5E7EB;
}

/* Form Styles */
.adjustment-form {
    margin-top: 20px;
}

.form-group {
    margin-bottom: 20px;
}

.form-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 8px;
}

.label-icon {
    font-size: 18px;
}

/* Type Selector */
.type-selector {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
}

.type-option {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px;
    background: #F9FAFB;
    border: 2px solid #E5E7EB;
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s;
    text-align: center;
}

.type-option:hover {
    border-color: #9CA3AF;
    background: #F3F4F6;
}

.type-option.active {
    border-color: #0A5C9E;
    background: #EBF5FF;
}

.option-icon {
    font-size: 24px;
    margin-bottom: 8px;
}

.option-text {
    font-weight: 600;
    color: #111827;
    margin-bottom: 4px;
}

.option-desc {
    font-size: 11px;
    color: #6B7280;
}

/* Amount Input */
.amount-input-group {
    position: relative;
    display: flex;
    align-items: center;
}

.currency-symbol {
    position: absolute;
    left: 12px;
    font-size: 16px;
    font-weight: 500;
    color: #6B7280;
    z-index: 1;
}

.amount-input {
    padding-left: 28px !important;
    font-size: 18px !important;
    font-weight: 600 !important;
    height: 48px;
}

.amount-input.error {
    border-color: #DC2626;
}

.amount-input.error:focus {
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.error-text {
    font-size: 12px;
    color: #DC2626;
    margin-top: 4px;
    display: block;
}

.form-text {
    font-size: 12px;
    margin-top: 4px;
    color: #6B7280;
}

/* Bill Summary */
.bill-summary {
    background: #F9FAFB;
    border-radius: 12px;
    padding: 16px;
    border: 1px solid #E5E7EB;
}

.summary-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
}

.summary-item:not(:last-child) {
    border-bottom: 1px dashed #E5E7EB;
}

.summary-item.total {
    margin-top: 4px;
    padding-top: 12px;
    border-top: 2px solid #E5E7EB;
    font-weight: 600;
}

.summary-label {
    color: #6B7280;
    font-size: 14px;
}

.summary-label.adjustment-label {
    color: #0A5C9E;
}

.summary-value {
    font-weight: 600;
    color: #111827;
}

.summary-value.total-value {
    font-size: 18px;
    color: #0A5C9E;
}

.text-danger {
    color: #DC2626 !important;
}

.text-success {
    color: #059669 !important;
}

/* Preview Card */
.preview-card {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 16px;
    border-radius: 12px;
    margin-top: 8px;
}

.preview-addon {
    background: #FEF2F2;
    border: 1px solid #FEE2E2;
}

.preview-discount {
    background: #F0FDF4;
    border: 1px solid #DCFCE7;
}

.preview-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
}

.preview-addon .preview-icon {
    background: #FEE2E2;
    color: #DC2626;
}

.preview-discount .preview-icon {
    background: #DCFCE7;
    color: #059669;
}

.preview-content h4 {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 4px 0;
}

.preview-addon .preview-content h4 {
    color: #DC2626;
}

.preview-discount .preview-content h4 {
    color: #059669;
}

.preview-content p {
    margin: 0;
    font-size: 13px;
    color: #4B5563;
}

/* Form Actions */
.form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 32px;
    padding-top: 20px;
    border-top: 2px solid #E5E7EB;
}

.btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.btn-icon {
    font-size: 16px;
}

.btn-primary {
    background: #0A5C9E;
    color: white;
}

.btn-primary:hover:not(:disabled) {
    background: #084A7D;
}

.btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn-outline-secondary {
    background: white;
    color: #4B5563;
    border: 1px solid #D1D5DB;
}

.btn-outline-secondary:hover {
    background: #F3F4F6;
}

/* Form Controls */
.form-control,
.form-select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #D1D5DB;
    border-radius: 8px;
    font-size: 14px;
    transition: all 0.2s;
    background: white;
}

.form-control:focus,
.form-select:focus {
    outline: none;
    border-color: #0A5C9E;
    box-shadow: 0 0 0 3px rgba(10, 92, 158, 0.1);
}

/* Responsive */
@media (max-width: 640px) {
    .adjustment-modal {
        padding: 16px;
    }

    .modal-header {
        flex-direction: column;
        gap: 12px;
    }

    .type-selector {
        grid-template-columns: 1fr;
    }

    .form-actions {
        flex-direction: column-reverse;
    }

    .btn {
        width: 100%;
        justify-content: center;
    }
}
</style>